import { useFetcherData } from "@prismicio/editor-support/Suspense"
import { type MediaAssetType } from "@prismicio/editor-ui"
import { useEffect, useRef } from "react"

import { throwConfigError, useEditorConfig } from "../../../EditorConfig"
import { getMedia, searchMedia } from "./mediaLibraryData"

interface AnalyticsMediaSearchArgs {
  assetType: MediaAssetType
  keyword: string
  tagIds: string[]
  mode: "select" | "browse"
}

export function useMediaLibraryAnalyticsMediaSearch(
  args: AnalyticsMediaSearchArgs,
) {
  const { assetType, keyword, mode, tagIds } = args

  const config = useEditorConfig()

  if (!config.mediaLibrary) throwConfigError("mediaLibrary")

  const { onAnalyticsEvent } = config

  const mediaCache = useFetcherData(searchMedia)
  const latestMediasPage = mediaCache[mediaCache.length - 1]

  const latestMediasPageRef = useRef(latestMediasPage)
  latestMediasPageRef.current = latestMediasPage

  useEffect(() => {
    // skip if no event is setup
    if (!onAnalyticsEvent) return
    // skip if keyword is empty
    if (!keyword) return
    // skip if there is no media page available
    if (!latestMediasPageRef.current) return
    // skip if more fresh results are available or requested
    if (latestMediasPageRef.current.keyword !== keyword) return

    const numberOfWords = String(keyword.split(" ").length)
    const numberOfResults = String(latestMediasPageRef.current.total)
    const tagsString = tagIds.join(",")

    onAnalyticsEvent("Media Library Search", {
      searchQuery: keyword,
      numberOfWords,
      numberOfResults,
      timeToGetResults: latestMediasPageRef.current.time,
      assetType,
      mode,
      tags: tagsString,
    })
  }, [keyword, assetType, mode, onAnalyticsEvent, tagIds])
}

interface AnalyticsMediaAddedToPageArgs {
  assetType: MediaAssetType
  mediaDialogOpen: boolean
}

export function useMediaLibraryAnalyticsMediaAddedToPage(
  args: AnalyticsMediaAddedToPageArgs,
) {
  const { assetType, mediaDialogOpen } = args

  const config = useEditorConfig()

  const allMediaPages = useFetcherData(searchMedia)

  // used to measure time since media dialog was opened
  const mediaDialogOpenTimerRef = useRef(0)
  useEffect(() => {
    if (mediaDialogOpen) {
      mediaDialogOpenTimerRef.current = performance.now()
    }
  }, [mediaDialogOpen])

  function onMediaAddedToPage(mediaId: string) {
    // skip if no event is setup
    if (!config.onAnalyticsEvent) return
    // skip if mediaLibrary is missing from config
    if (!config.mediaLibrary) return

    // last page in cache is the "latest" one
    const latestMediasPage = allMediaPages[allMediaPages.length - 1]

    // skip if there is no media page available
    if (!latestMediasPage) return

    const { keyword, uploaderId, tags } = latestMediasPage

    const medias = getMedia(allMediaPages, keyword, assetType, uploaderId, tags)

    const numberOfWords = String(keyword.split(" ").length)
    const numberOfResults = String(latestMediasPage.total)
    const mediaIndex = medias.findIndex((media) => media.id === mediaId)
    const searchPosition = String(mediaIndex + 1)
    const mediaIds = [mediaId].join(",")
    const timeToAddMediaToPage = (
      performance.now() - mediaDialogOpenTimerRef.current
    ).toFixed(0)

    config.onAnalyticsEvent("Media Added To Page", {
      assetType,
      searchQuery: keyword,
      numberOfWords,
      numberOfResults,
      searchPosition,
      mediaIds,
      timeToAddMediaToPage,
      tags,
    })
  }

  return onMediaAddedToPage
}
